import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { orderService } from '../../services/api';
import { addressToText } from '../../utils/formatAddress';

interface OrderFormProps {
  onOrderCreated?: (order: any) => void;
}

interface Property {
  id: string;
  address: string;
}

interface CertificateType {
  id: string;
  name: string;
  description: string;
  estimatedDays: number;
  basePrice?: number;
}

const CERTIFICATE_TYPES: CertificateType[] = [
  {
    id: 'gas_safety',
    name: 'Gas Safety Certificate',
    description: 'Required annually for all rental properties with gas appliances',
    estimatedDays: 3,
    basePrice: 79,
  },
  {
    id: 'epc',
    name: 'Energy Performance Certificate',
    description: 'Required when marketing or letting a property',
    estimatedDays: 2,
    basePrice: 49,
  },
  {
    id: 'eicr',
    name: 'Electrical Installation Certificate',
    description: 'Required every 5 years for rental properties',
    estimatedDays: 5,
    basePrice: 149,
  },
  {
    id: 'pat_testing',
    name: 'PAT Testing',
    description: 'Portable Appliance Testing for safety compliance',
    estimatedDays: 3,
    basePrice: 99,
  },
];

export default function OrderForm({ onOrderCreated }: OrderFormProps) {
  const navigate = useNavigate();
  const { propertyId: urlPropertyId } = useParams<{ propertyId?: string }>();
  const [searchParams] = useSearchParams();
  const queryPropertyId = searchParams.get('propertyId');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form state
  const [properties, setProperties] = useState<Property[]>([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState(queryPropertyId || urlPropertyId || '');
  const queryCertType = searchParams.get('certType');
  const [selectedCertType, setSelectedCertType] = useState(queryCertType || '');
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
  const [notes, setNotes] = useState('');
  const [tenantInfo, setTenantInfo] = useState({
    name: '',
    phone: '',
  });
  const [accessDetails, setAccessDetails] = useState({
    contactName: '',
    contactPhone: '',
    accessInstructions: '',
    accessMethod: 'keys_at_door'
  });

  useEffect(() => {
    loadProperties();
  }, []);

  const loadProperties = async () => {
    try {
      setLoading(true);
      const response = await orderService.getProperties();

      if (!response.data) throw new Error('Failed to load properties');

      const propList = response.data?.data || response.data || [];
      // Filter to only show properties with valid addresses (exclude raw IDs)
      const validProperties = propList
        .filter((prop: any) => prop.address?.line1 || prop.name)
        .map((prop: any) => ({
          id: prop.id,
          address: prop.address?.line1 || prop.name,
        }));
      setProperties(validProperties);

      // Auto-select property if passed via query param
      if (queryPropertyId && propList.length > 0) {
        setSelectedPropertyId(queryPropertyId);
      }
    } catch (err) {
      console.error('[OrderForm] Load properties failed:', err);
      setError('Unable to load properties. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Validation
    if (!selectedPropertyId) {
      setError('Please select a property');
      return;
    }
    if (!selectedCertType) {
      setError('Please select a certificate type');
      return;
    }
    if (!tenantInfo.name || tenantInfo.name.trim() === '') {
      setError('Tenant name is required');
      return;
    }
    if (!tenantInfo.phone || tenantInfo.phone.trim() === '') {
      setError('Tenant phone number is required');
      return;
    }
    if (!/^[\d\s\-\+\(\)]{10,}$/.test(tenantInfo.phone.replace(/\s/g, ''))) {
      setError('Please provide a valid phone number');
      return;
    }
    if (!accessDetails.contactName) {
      setError('Please provide contact name for engineer access');
      return;
    }
    if (!accessDetails.contactPhone) {
      setError('Please provide contact phone number');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const orderPayload = {
        certificateType: selectedCertType,
        propertyId: selectedPropertyId,
        tenantName: tenantInfo.name,
        tenantPhone: tenantInfo.phone,
        dateRange: {
          startDate: dateRange.startDate || '',
          endDate: dateRange.endDate || '',
        },
        accessDetails: {
          contactName: accessDetails.contactName,
          contactPhone: accessDetails.contactPhone,
          accessInstructions: accessDetails.accessInstructions,
          accessMethod: accessDetails.accessMethod,
        },
        notes: notes,
      };

      const response = await orderService.createOrder(orderPayload);

      if (!response.data) {
        throw new Error('Failed to create order');
      }

      const data = response.data;
      const orderId = data.data?.id || data.id;

      setSuccess(`Order created successfully! Order ID: ${orderId}`);

      // Clear form
      setSelectedPropertyId(queryPropertyId || '');
      setSelectedCertType(queryCertType || '');
      setDateRange({ startDate: '', endDate: '' });
      setNotes('');
      setTenantInfo({
        name: '',
        phone: '',
      });
      setAccessDetails({
        contactName: '',
        contactPhone: '',
        accessInstructions: '',
        accessMethod: 'keys_at_door'
      });

      // Call callback or navigate
      if (onOrderCreated && data.data) {
        onOrderCreated(data.data);
      } else {
        // Navigate to order detail or history
        setTimeout(() => {
          navigate(`/certificate-orders/${orderId}`);
        }, 1500);
      }
    } catch (err) {
      console.error('[OrderForm] Submit failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to create order');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedCert = CERTIFICATE_TYPES.find((c) => c.id === selectedCertType);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-pulse flex flex-col items-center">
          <div className="h-12 w-12 bg-emerald-200 rounded-full mb-4"></div>
          <p className="text-gray-600">Loading properties...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold text-gray-900 mb-6 md:text-3xl">
        Order New Certificate
      </h2>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-green-800 text-sm">{success}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Property Selection */}
        <div>
          <label htmlFor="property" className="block text-sm font-medium text-gray-900 mb-2">
            Select Property <span className="text-red-600">*</span>
          </label>
          {properties.length === 0 ? (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-amber-800 text-sm mb-3">No properties found.</p>
              <button
                type="button"
                onClick={() => navigate('/add-property')}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-emerald-600 hover:bg-emerald-700"
              >
                Add a Property
              </button>
            </div>
          ) : (
            <select
              id="property"
              value={selectedPropertyId}
              onChange={(e) => setSelectedPropertyId(e.target.value)}
              className="w-full px-3 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-gray-900 text-base"
            >
              <option value="">-- Select a property --</option>
              {properties.map((prop) => (
                <option key={prop.id} value={prop.id}>
                  {addressToText(prop.address)}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Certificate Type Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-3">
            Certificate Type <span className="text-red-600">*</span>
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {CERTIFICATE_TYPES.map((cert) => (
              <button
                key={cert.id}
                type="button"
                onClick={() => setSelectedCertType(cert.id)}
                className={`p-4 rounded-lg border-2 transition text-left ${
                  selectedCertType === cert.id
                    ? 'border-emerald-500 bg-emerald-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="font-medium text-gray-900 text-sm">{cert.name}</div>
                <div className="text-xs text-gray-600 mt-1">{cert.description}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Certificate Details Panel */}
        {selectedCert && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
            <h3 className="font-semibold text-emerald-900 mb-2">{selectedCert.name}</h3>
            <p className="text-sm text-emerald-700">{selectedCert.description}</p>
          </div>
        )}

        {/* Date Range (Optional) - Unconfirmed until partner confirmation */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="startDate" className="block text-sm font-medium text-gray-900 mb-2">
              Requested Start Date <span className="text-gray-500 text-xs">(Unconfirmed)</span>
            </label>
            <input
              type="date"
              id="startDate"
              value={dateRange.startDate}
              onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
              className="w-full px-3 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
          <div>
            <label htmlFor="endDate" className="block text-sm font-medium text-gray-900 mb-2">
              Requested End Date <span className="text-gray-500 text-xs">(Unconfirmed)</span>
            </label>
            <input
              type="date"
              id="endDate"
              value={dateRange.endDate}
              onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
              className="w-full px-3 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
        </div>

        {/* Tenant Information */}
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <h3 className="font-semibold text-purple-900 mb-4">👥 Tenant Information</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="tenantName" className="block text-sm font-medium text-gray-900 mb-2">
                Tenant Name <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                id="tenantName"
                value={tenantInfo.name}
                onChange={(e) => setTenantInfo({ ...tenantInfo, name: e.target.value })}
                placeholder="Name of tenant occupying the property"
                className="w-full px-3 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
              />
            </div>
            <div>
              <label htmlFor="tenantPhone" className="block text-sm font-medium text-gray-900 mb-2">
                Tenant Phone <span className="text-red-600">*</span>
              </label>
              <input
                type="tel"
                id="tenantPhone"
                value={tenantInfo.phone}
                onChange={(e) => setTenantInfo({ ...tenantInfo, phone: e.target.value })}
                placeholder="+44 7000 000000"
                className="w-full px-3 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
              />
            </div>
          </div>
        </div>

        {/* Access Details for Engineer */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-semibold text-blue-900 mb-4">👤 Access Details for Engineer</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="contactName" className="block text-sm font-medium text-gray-900 mb-2">
                Contact Name <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                id="contactName"
                value={accessDetails.contactName}
                onChange={(e) => setAccessDetails({ ...accessDetails, contactName: e.target.value })}
                placeholder="Name of person at property"
                className="w-full px-3 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
            <div>
              <label htmlFor="contactPhone" className="block text-sm font-medium text-gray-900 mb-2">
                Contact Phone <span className="text-red-600">*</span>
              </label>
              <input
                type="tel"
                id="contactPhone"
                value={accessDetails.contactPhone}
                onChange={(e) => setAccessDetails({ ...accessDetails, contactPhone: e.target.value })}
                placeholder="+44 7000 000000"
                className="w-full px-3 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Access Method <span className="text-red-600">*</span>
            </label>
            <select
              value={accessDetails.accessMethod}
              onChange={(e) => setAccessDetails({ ...accessDetails, accessMethod: e.target.value })}
              className="w-full px-3 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
            >
              <option value="keys_at_door">Keys left at door</option>
              <option value="landlord_present">Landlord will be present</option>
              <option value="tenant_contact">Tenant will provide access</option>
              <option value="keypad">Keypad code</option>
              <option value="other">Other (specify below)</option>
            </select>
          </div>

          <div className="mt-4">
            <label htmlFor="accessInstructions" className="block text-sm font-medium text-gray-900 mb-2">
              Access Instructions
            </label>
            <textarea
              id="accessInstructions"
              value={accessDetails.accessInstructions}
              onChange={(e) => setAccessDetails({ ...accessDetails, accessInstructions: e.target.value })}
              placeholder="E.g., 'Keys in blue box under potted plant', 'Ring bell then wait 5 mins', etc."
              rows={2}
              className="w-full px-3 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
        </div>

        {/* Notes */}
        <div>
          <label htmlFor="notes" className="block text-sm font-medium text-gray-900 mb-2">
            Additional Notes (Optional)
          </label>
          <textarea
            id="notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any special requirements or notes for the certificate provider..."
            rows={3}
            className="w-full px-3 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
          />
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={submitting || !selectedPropertyId || !selectedCertType}
          className={`w-full py-3 px-4 rounded-lg font-medium text-white transition ${
            submitting || !selectedPropertyId || !selectedCertType
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800'
          }`}
        >
          {submitting ? (
            <span className="flex items-center justify-center">
              <span className="animate-spin mr-2">⏳</span>
              Creating Order...
            </span>
          ) : (
            'Create Order'
          )}
        </button>
      </form>
    </div>
  );
}
